// src/components/admin/tabs/ActivityLogTab.jsx
// 操作日志：动作/状态/时间筛选 + 分页表，点击行展开 details/IP/UA。
// 从 AdminDashboard.jsx 抽取；查询、分页、展开逻辑原样保留。
import React, { useState, useEffect, useCallback } from "react";
import { fetchActivity } from "../../../services/adminService";
import {
  Panel, EmptyText, StatusBadge, SecondaryButton, FilterInput, FilterSelect, AdminScopeFilterBar,
} from "../adminUi.jsx";
import { displayUserId, useAdminScope } from "../adminUtils";

const ACTION_OPTIONS = [
  '', 'SAVE_STORYBOARD', 'GENERATE_IMAGE', 'GENERATE_VIDEO', 'CANCEL_VIDEO',
  'ERASE_SUBTITLE', 'INPAINT', 'WANX_EDIT', 'UPSCALE', 'LOGIN', 'LOGOUT',
];
const STATUS_OPTIONS = ['', 'submitted', 'success', 'failed'];

// 日志状态语义色；未知状态由 StatusBadge 回退为 muted
const LOG_STATUS_COLORS = {
  success: 'var(--color-success)',
  failed: 'var(--color-error)',
  submitted: 'var(--color-info)',
};

const ActivityLogTab = ({ projectId, projectOptions }) => {
  const [filters, setFilters] = useState({ action: '', status: '', startTime: '', endTime: '' });
  const [scope, setScope] = useAdminScope(projectId);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [data, setData] = useState({ content: [], totalElements: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // 仅用于首屏骨架屏展示，不影响数据逻辑
  const [loadedOnce, setLoadedOnce] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const f = { ...filters };
      if (scope.projectId) f.projectId = scope.projectId;
      if (scope.userId) f.userId = scope.userId;
      if (f.startTime) f.startTime = new Date(f.startTime).getTime();
      if (f.endTime) f.endTime = new Date(f.endTime).getTime();
      const res = await fetchActivity(f, page, pageSize);
      setData(res);
      setLoadedOnce(true);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [filters, page, pageSize, scope.projectId, scope.userId]);

  useEffect(() => { load(); }, [load]);

  const firstLoading = loading && !loadedOnce;

  return (
    <div>
      <AdminScopeFilterBar
        scope={scope}
        setScope={setScope}
        projectOptions={projectOptions}
        onRefresh={() => { setPage(0); load(); }}
        loading={loading}
      >
        <FilterSelect label="动作" value={filters.action} options={ACTION_OPTIONS} onChange={(v) => setFilters(f => ({ ...f, action: v }))} />
        <FilterSelect label="状态" value={filters.status} options={STATUS_OPTIONS} onChange={(v) => setFilters(f => ({ ...f, status: v }))} />
        <FilterInput label="起始" type="datetime-local" value={filters.startTime} onChange={(v) => setFilters(f => ({ ...f, startTime: v }))} />
        <FilterInput label="结束" type="datetime-local" value={filters.endTime} onChange={(v) => setFilters(f => ({ ...f, endTime: v }))} />
      </AdminScopeFilterBar>

      <Panel title="操作日志" sub="点击行展开详情">
        <div className="flex justify-between items-center mb-3 text-xs" style={{ color: 'var(--color-text-muted)' }}>
          <span>共 <span className="font-mono-num">{data.totalElements}</span> 条</span>
          <div className="flex items-center gap-2">
            <SecondaryButton disabled={page === 0} onClick={() => setPage(p => p - 1)}>上一页</SecondaryButton>
            <span className="font-mono-num text-xs">{page + 1} / {data.totalPages || 1}</span>
            <SecondaryButton disabled={page >= (data.totalPages || 1) - 1} onClick={() => setPage(p => p + 1)}>下一页</SecondaryButton>
          </div>
        </div>
        {firstLoading ? (
          <div className="space-y-2">
            <div className="skeleton h-3 w-full" />
            <div className="skeleton h-3 w-11/12" />
            <div className="skeleton h-3 w-4/5" />
            <div className="skeleton h-3 w-2/3" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-label" style={{ textAlign: 'left' }}>
                  <th className="font-medium pb-2.5 pr-3">时间</th>
                  <th className="font-medium pb-2.5 pr-3">用户</th>
                  <th className="font-medium pb-2.5 pr-3">动作</th>
                  <th className="font-medium pb-2.5 pr-3">Provider</th>
                  <th className="font-medium pb-2.5 pr-3">状态</th>
                  <th className="font-medium pb-2.5 pr-3">耗时</th>
                  <th className="font-medium pb-2.5 pr-3">错误</th>
                </tr>
              </thead>
              <tbody>
                {data.content.length === 0 ? (
                  <tr><td colSpan={7}><EmptyText text="暂无" /></td></tr>
                ) : data.content.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                      className="cursor-pointer"
                      style={{ borderTop: '1px solid var(--color-border-secondary)' }}>
                      <td className="py-2 pr-3 text-2xs font-mono-num whitespace-nowrap" style={{ color: 'var(--color-text-muted)' }}>
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                      <td className="py-2 pr-3 text-xs">{displayUserId(row.user_id)}</td>
                      <td className="py-2 pr-3 font-semibold text-xs">{row.action}</td>
                      <td className="py-2 pr-3 text-xs" style={{ color: 'var(--color-text-secondary)' }}>{row.provider || '-'}</td>
                      <td className="py-2 pr-3">
                        <StatusBadge label={row.status || '-'} color={LOG_STATUS_COLORS[row.status]} />
                      </td>
                      <td className="py-2 pr-3 text-xs font-mono-num">{row.duration_ms != null ? `${row.duration_ms}ms` : '-'}</td>
                      <td className="py-2 pr-3 text-xs max-w-xs truncate" style={{ color: 'var(--color-error)' }}>{row.error_message || ''}</td>
                    </tr>
                    {expandedId === row.id && (
                      <tr>
                        <td colSpan={7} className="py-2">
                          <div className="rounded-md p-2.5 animate-in fade-in duration-200" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-secondary)' }}>
                            <pre className="whitespace-pre-wrap break-all max-h-72 overflow-auto text-2xs font-mono-num" style={{ color: 'var(--color-text-secondary)' }}>
{JSON.stringify({
  projectId: row.project_id, episodeId: row.episode_id, shotId: row.shot_id,
  resourceType: row.resource_type, ip: row.ip_address, ua: row.user_agent,
  details: row.details ? (() => { try { return JSON.parse(row.details); } catch { return row.details; } })() : null,
}, null, 2)}
                            </pre>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default ActivityLogTab;
